import {
  type BreadcrumbSegment,
  type SideNavSection,
  SideNavLayout,
} from '@dittolive/anvil'
import { TenantContext } from 'components/tenant'
import { Suspense, useContext } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'

import SideNavUserMenu from '../navigationBar/SideNavUserMenu'
import TenantActionMenu from '../navigationBar/TenantActionMenu'

type Props = {
  /** Sidebar navigation sections. */
  navSections?: SideNavSection[]
  /** URL for a back button in the sidebar, replacing the logo. */
  backTo?: string
  /** Label for the back button. */
  backLabel?: string
  /** Breadcrumb segments for the top bar. */
  breadcrumbs?: BreadcrumbSegment[]
  /** Render the sidebar collapsed on first paint. */
  defaultCollapsed?: boolean
  /** Whether to show the user menu in the sidebar footer. Defaults to true. */
  showUserMenu?: boolean
  /**
   * Whether to show a tenant switcher as the first breadcrumb segment.
   * When true, the first breadcrumb will be an ActionMenu for switching organizations.
   * Defaults to false.
   */
  showTenantBreadcrumb?: boolean
  /** The current tenant name to display in the breadcrumb. Required when showTenantBreadcrumb is true. */
  tenantBreadcrumbName?: string
  children?: React.ReactNode
}

/**
 * Shared shell wrapping SideNavLayout with the cloud portal's standard
 * chrome: breadcrumbs in the top bar and the user menu in the sidebar footer.
 */
export default function PortalShell({
  navSections,
  backTo,
  backLabel,
  breadcrumbs = [],
  defaultCollapsed,
  showUserMenu = true,
  showTenantBreadcrumb = false,
  tenantBreadcrumbName,
  children,
}: Props) {
  const { tenant, tenantHome, updateTenant, getTenantHome } =
    useContext(TenantContext)
  const navigate = useNavigate()
  const location = useLocation()

  const handleChangeTenant = (newTenant: string | null) => {
    updateTenant(newTenant)

    // Try to preserve the current route when switching tenants.
    // Only works for tenant-level pages (e.g. /:org/databases, /databases),
    // not app-level pages where the app won't exist in the new tenant.
    const pathname = location.pathname

    // Route prefixes that exist in the org tenant context
    // (e.g. /:org/databases, /:org/settings, /:org/access).
    const orgAreas = [
      'databases',
      'activity',
      'new-database',
      'settings',
      'access',
    ]
    // Route prefixes that exist in the personal tenant context
    // (e.g. /databases, /organizations, /settings).
    const personalAreas = [
      'databases',
      'organizations',
      'settings',
      'new-database',
      'employee-groups',
    ]

    // Areas that exist in both tenants, keyed by which direction we're switching.
    const sharedAreas = newTenant ? orgAreas : personalAreas

    // Check if we're on an org-level page: /:orgSlug/<area>
    if (tenant) {
      const orgPrefix = `/${tenant}/`
      if (pathname.startsWith(orgPrefix)) {
        const area = pathname.slice(orgPrefix.length)
        // Only preserve if the same area also exists in the target tenant.
        if (sharedAreas.some((a) => area === a || area.startsWith(`${a}/`))) {
          if (newTenant) {
            navigate(`/${newTenant}/${area}`)
          } else {
            // The personal tenant only registers top-level routes (e.g.
            // /settings, not /settings/general), so drop nested segments.
            navigate(`/${area.split('/')[0]}`)
          }
          return
        }
      }
    }

    // Check if we're on a personal tenant-level page: /databases, /organizations, etc.
    if (!tenant) {
      const firstSegment = pathname.split('/')[1]
      if (firstSegment && sharedAreas.some((a) => firstSegment === a)) {
        navigate(
          newTenant ? `/${newTenant}/${firstSegment}` : `/${firstSegment}`,
        )
        return
      }
    }

    const redirectPath = getTenantHome(newTenant)
    navigate(redirectPath)
  }

  // Build the final breadcrumbs with the tenant menu if enabled
  const finalBreadcrumbs: BreadcrumbSegment[] = showTenantBreadcrumb
    ? [
        {
          text: tenantBreadcrumbName || 'Personal',
          render: () => (
            <TenantActionMenu
              currentTenant={tenant}
              currentTenantName={tenantBreadcrumbName || 'Personal'}
              onChangeTenant={handleChangeTenant}
            />
          ),
        },
        ...breadcrumbs,
      ]
    : breadcrumbs

  return (
    <SideNavLayout
      navSections={navSections}
      backTo={backTo}
      backLabel={backLabel}
      breadcrumbs={finalBreadcrumbs}
      defaultCollapsed={defaultCollapsed}
      logoTo={tenantHome || '/'}
      pathname={location.pathname}
      linkAs={NavLink}
      footerContent={
        showUserMenu
          ? (collapsed) => (
              <Suspense fallback={null}>
                <SideNavUserMenu collapsed={collapsed} />
              </Suspense>
            )
          : undefined
      }
    >
      {children}
    </SideNavLayout>
  )
}
